import * as React from 'react';

type KeyboardTrapRef = {
    ref: React.RefObject<any>;
};

export default function useKeyboardTrap(closeModal: () => void, skipFocusCloseButton?: boolean): KeyboardTrapRef {
    const trapRef = React.useRef<HTMLElement>();

    function findFocusableElements(): HTMLElement[] {
        return Array.from(
            trapRef.current!.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]):not([tabindex="-1"]), label[tabindex="0"], textarea:not([disabled]), input:not([disabled]), input[type="text"], input[type="number"], input[type="radio"], input[type="checkbox"], select:not([disabled])',
            ),
        );
    }

    React.useEffect(() => {
        if (!trapRef.current) return;

        const focusableModalElements = findFocusableElements();
        const firstElement = skipFocusCloseButton ? focusableModalElements[1] : focusableModalElements[0];
        firstElement?.focus();

        function keyListener(e: KeyboardEvent) {
            if (!trapRef.current) return;
            const listener = keyListenersMap.get(e.key);
            if (listener) listener(e);
        }

        document.addEventListener('keydown', keyListener);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', keyListener);
            document.body.style.overflow = 'visible';
        };
    }, []); //eslint-disable-line

    const handleTabKey = (e: KeyboardEvent) => {
        if (!trapRef.current) return;

        const focusable = findFocusableElements();
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
            last.focus();
            e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
            first.focus();
            e.preventDefault();
        }
    };

    const keyListenersMap = new Map([
        // escape key - by convention this should close a modal/overlay.
        ['Escape', closeModal],
        ['Tab', handleTabKey],
    ]);

    return { ref: trapRef };
}
